import { AppBar, Box, useMediaQuery, useTheme } from '@mui/material';
import {
  useAppSelector,
  useAuth,
  useDictionary,
  useDispatchApp,
  useIsDesktop,
} from 'Hooks';
import { useCallback, useEffect, useState } from 'react';
import { ExpandedBurgerMenuView } from './ExpandedBurgerMenuView';
import { ShrinkBurgerMenuView } from './ShrinkBurgerMenuView';
import { MenuOutro } from './MenuOutro';
import { getBurgerMenuItems } from 'API/Home/burgerMenuItems';
import { setBurgerMenuOpen, setMenuItems } from 'Store/MenuItemsSlice';
import { IBurgerMenuItem } from 'API/Home/burgerMenuItems';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { representativesAllowedUrls } from 'API';
import { setIsLawyer } from 'Store';
import { useNavigate } from 'react-router-dom';
import { getIsLawyer, getIsLawyerAndUser } from 'Utils/lawyers';
import { useIsLawyerArea } from 'Hooks/useIsLawyerArea';

interface IBurgerMenu {
  top?: string;
}
export const BurgerMenu = ({ top }: IBurgerMenu) => {
  const { isAuth, isOtp } = useAuth();
  const { inProgress } = useMsal();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [expandedMenuItem, setExpandedMenuItem] =
    useState<IBurgerMenuItem | null>(null);
  const [lawyersName, setLawyersName] = useState<string>('');

  const isLawyerArea = useIsLawyerArea();

  const isDesktop = useIsDesktop();

  const dictionary = useDictionary(Dictionary, DICTIONARY.BurgerMenu);

  const theme = useTheme();
  const isScreenXl = useMediaQuery(theme.breakpoints.up('xl'));

  const representativeName = useAppSelector(
    ({ imposter: { representativeName } }) => representativeName,
  );

  const dispatch = useDispatchApp();

  const initMenuItems = useCallback(async () => {
    const isLawyer = getIsLawyer();

    dispatch(setIsLawyer(isLawyer));
    setLawyersName(isLawyer ? `${representativeName}`.trim() : '');

    const menuItemsResult = await getBurgerMenuItems(
      getIsLawyerAndUser() ? isLawyerArea : isLawyer,
    );

    if (menuItemsResult) dispatch(setMenuItems(menuItemsResult));
  }, [
    dispatch,
    isLawyerArea,
    representativeName,
    getIsLawyer,
    getIsLawyerAndUser,
  ]);

  useEffect(() => {
    if (inProgress === InteractionStatus.None && (isAuth || isOtp))
      initMenuItems();
  }, [inProgress, initMenuItems, isAuth, isOtp]);

  useEffect(() => {
    dispatch(setBurgerMenuOpen(isOpen));
    return () => {
      dispatch(setBurgerMenuOpen(false));
    };
  }, [dispatch, isOpen]);

  const openMenuBarHandler = (e: React.MouseEvent) => {
    if (
      !isOpen &&
      !isScreenXl &&
      (e.target as HTMLElement).id === 'burger-menu'
    )
      setIsOpen(true);
  };

  const isLaptopViewOrClosedMenu = isOpen || isScreenXl;

  const navigate = useNavigate();

  useEffect(() => {
    if (getIsLawyerAndUser() ? isLawyerArea : getIsLawyer()) {
      if (
        !representativesAllowedUrls.some((url) =>
          window.location.pathname.includes(url),
        )
      ) {
        navigate('/lawyers-lobby');
      }
    } else if (window.location.pathname.includes('/lawyers-lobby')) {
      navigate('/');
    }
  }, [isLawyerArea, navigate]);

  return !isDesktop ? null : (
    <>
      {!isScreenXl && isOpen && (
        <Box
          sx={{
            width: '100vw',
            height: '100%',
            position: 'absolute',
            top: '0',
            right: '0',
            backgroundColor: 'transparent',
            zIndex: '9',
          }}
          onClick={() => setIsOpen(false)}
        />
      )}
      <Box
        sx={{
          mt: top,
          width: '300px',
          mr: isScreenXl ? '0' : isOpen ? '0' : '-228px',
          ml: isScreenXl ? '0' : isOpen ? '-228px' : '0',
          flexShrink: 0,
          transition: '0.25s',
        }}
      >
        <AppBar
          component={'nav'}
          sx={{
            position: 'sticky',
            width: isLaptopViewOrClosedMenu ? '300px' : '72px',
            height: '100vh',
            zIndex: 1100,
            top: '80px',
            right: '0',
            left: 'auto',
            background:
              'linear-gradient(176.1deg, rgb(8, 30, 84) -4.18%, rgb(40, 96, 234) 13.93%)',
            transition: '0.25s',
            cursor: !isOpen && !isScreenXl ? 'pointer' : undefined,
            overflowY: 'scroll',
            overflowX: 'visible',
            '::-webkit-scrollbar': {
              display: 'none',
            },
          }}
          onClick={openMenuBarHandler}
          id='burger-menu'
          role={'navigation'}
          aria-label={dictionary.menuAriaLabel}
        >
          {isScreenXl ? (
            <ExpandedBurgerMenuView lawyersName={lawyersName} />
          ) : isOpen ? (
            <ExpandedBurgerMenuView
              closeMenu={() => setIsOpen(false)}
              expandedMenuItem={expandedMenuItem}
              lawyersName={lawyersName}
            />
          ) : (
            <ShrinkBurgerMenuView
              setExpandedMenuItem={(item: IBurgerMenuItem) => {
                if (item.subItems && item.subItems.length > 0) {
                  setIsOpen(true);
                }
                setExpandedMenuItem(item);
              }}
            />
          )}
          <MenuOutro isLaptopViewOrClosedMenu={isLaptopViewOrClosedMenu} />
        </AppBar>
      </Box>
    </>
  );
};
